import React, { useState, useEffect, useRef } from 'react';
import { Search, Loader2, Eye, ChevronRight, Truck, Package, Check, X, Trash2, AlertTriangle, Printer, FileText, Plus, Minus, Edit3, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';

import { API_URL, BACKEND_URL } from '../../config/api';
const API = `${BACKEND_URL}/api`;

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [editingItems, setEditingItems] = useState(false);
  const [editedItems, setEditedItems] = useState([]);
  const printRef = useRef(null);

  useEffect(() => {
    fetchOrders();
  }, [statusFilter]);

  useEffect(() => {
    if (selectedOrder) {
      setTrackingNumber(selectedOrder.tracking_number || '');
      setEditedItems(selectedOrder.items || []);
      setEditingItems(false);
    }
  }, [selectedOrder?.order_id]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const url = statusFilter 
        ? `${API}/admin/orders?status=${statusFilter}`
        : `${API}/admin/orders`;
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setOrders(data);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    setUpdatingStatus(true);
    try {
      const payload = { status };
      
      if (status === 'shipped' && trackingNumber.trim()) {
        payload.tracking_number = trackingNumber.trim();
      }
      
      const response = await fetch(`${API}/admin/orders/${orderId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(status === 'shipped' ? 'Commande expédiée - Email envoyé au client' : 'Statut mis à jour');
        fetchOrders();
        if (selectedOrder?.order_id === orderId) {
          setSelectedOrder(prev => ({ ...prev, status, tracking_number: payload.tracking_number || prev.tracking_number }));
        }
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setUpdatingStatus(false);
    }
  };
  
  const updateTrackingNumber = async (orderId) => {
    if (!trackingNumber.trim()) {
      toast.error('Veuillez entrer un numéro de suivi');
      return;
    }
    
    try {
      const response = await fetch(`${API}/admin/orders/${orderId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tracking_number: trackingNumber.trim() })
      });

      if (response.ok) {
        toast.success('Numéro de suivi enregistré');
        fetchOrders();
        if (selectedOrder?.order_id === orderId) {
          setSelectedOrder(prev => ({ ...prev, tracking_number: trackingNumber.trim() }));
        }
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (orderId) => {
    try {
      const response = await fetch(`${API}/admin/orders/${orderId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Commande supprimée');
        setOrders(prev => prev.filter(o => o.order_id !== orderId));
        if (selectedOrder?.order_id === orderId) {
          setSelectedOrder(null);
        }
        setDeleteConfirm(null);
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  // Modification des articles
  const updateItemQuantity = (index, delta) => {
    setEditedItems(prev => {
      const newItems = [...prev];
      const newQty = Math.max(0, (newItems[index].quantity || 1) + delta);
      if (newQty === 0) {
        return newItems.filter((_, i) => i !== index);
      }
      newItems[index] = { ...newItems[index], quantity: newQty };
      return newItems;
    });
  };

  const removeItem = (index) => {
    setEditedItems(prev => prev.filter((_, i) => i !== index));
  };

  const saveEditedItems = async () => {
    try {
      // Recalculer le total
      const newTotal = editedItems.reduce((sum, item) => {
        return sum + (item.price || 0) * (item.quantity || 1);
      }, 0);

      const response = await fetch(`${API}/admin/orders/${selectedOrder.order_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          items: editedItems,
          total_amount: newTotal
        })
      });

      if (response.ok) {
        toast.success('Commande modifiée');
        setEditingItems(false);
        fetchOrders();
        setSelectedOrder(prev => ({ ...prev, items: editedItems, total_amount: newTotal }));
      } else {
        toast.error('Erreur lors de la modification');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  // Impression PDF
  const handlePrint = () => {
    const printContent = generatePrintContent(selectedOrder, 'invoice');
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  // Bon de préparation
  const handlePreparationSheet = () => {
    const printContent = generatePrintContent(selectedOrder, 'preparation');
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  const generatePrintContent = (order, type) => {
    const isInvoice = type === 'invoice';
    const title = isInvoice ? 'Facture' : 'Bon de Préparation';
    
    // Legal information - Micro-entreprise assujettie à TVA
    const legalInfo = {
      companyName: 'VAUDIN GAEL',
      tradeName: 'FGS - ALARME MONTPELLIER',
      siret: '485 272 587 00059',
      tvaNumber: 'FR63485272587',
      rcs: '485 272 587 R.C.S. Montpellier',
      address: 'Bureau 3, 6 B BD Berthelot, 34000 MONTPELLIER',
      phone: '07 45 04 09 29',
      email: 'contact@maprotection-ia.fr',
      website: 'maprotection-ia.fr',
      apeCode: '80.10Z',
      legalForm: 'Entrepreneur individuel'
    };

    // Calculate totals with promo
    const subtotalHT = order.items?.reduce((sum, item) => {
      const itemTotal = (item.price || 0) * (item.quantity || 1);
      const servicesTotal = item.services?.reduce((s, srv) => s + (srv.price || 0), 0) || 0;
      return sum + itemTotal + servicesTotal;
    }, 0) || 0;
    
    const promoDiscount = order.promo_discount || 0;
    const promoCode = order.promo_code || null;
    const shippingCostTTC = order.shipping_cost || 0;
    const tvaRate = order.has_installation ? 10 : 20;
    
    // Calculate shipping HT and TVA
    const shippingHT = shippingCostTTC / (1 + tvaRate / 100);
    const shippingTVA = shippingCostTTC - shippingHT;
    
    const subtotalAfterPromo = subtotalHT - promoDiscount;
    const productTVA = subtotalAfterPromo * (tvaRate / 100);
    const totalTVA = productTVA + shippingTVA;
    const totalTTC = subtotalAfterPromo + totalTVA + shippingHT;
    
    const itemsHtml = order.items?.map(item => {
      // Build services list for preparation slip
      const servicesList = item.services?.map(s => s.name).join(', ') || '';
      
      return `
      <tr>
        <td style="padding: 10px; border-bottom: 1px solid #e5e5e5;">
          <strong>${item.name || item.product_name || 'Produit'}</strong>
          ${item.color ? `<br><span style="color: #666; font-size: 12px;">Couleur: ${getColorLabel(item.color)}</span>` : ''}
          ${servicesList ? `<br><span style="color: #10b981; font-size: 12px; font-weight: 500;">Options: ${servicesList}</span>` : ''}
        </td>
        <td style="padding: 10px; border-bottom: 1px solid #e5e5e5; text-align: center;">${item.quantity || 1}</td>
        ${isInvoice ? `
          <td style="padding: 10px; border-bottom: 1px solid #e5e5e5; text-align: right;">${(item.price || 0).toFixed(2)} €</td>
          <td style="padding: 10px; border-bottom: 1px solid #e5e5e5; text-align: right; font-weight: bold;">${((item.price || 0) * (item.quantity || 1)).toFixed(2)} €</td>
        ` : `
          <td style="padding: 10px; border-bottom: 1px solid #e5e5e5; text-align: center;">☐</td>
        `}
      </tr>
      ${!isInvoice && servicesList ? `
      <tr style="background: #f0fdf4;">
        <td colspan="3" style="padding: 8px 10px; border-bottom: 1px solid #e5e5e5;">
          <span style="color: #10b981; font-size: 12px;">
            ⚡ <strong>Services à préparer:</strong> ${item.services?.map(s => `<span style="display: inline-block; margin: 2px 4px; padding: 2px 8px; background: #10b981; color: white; border-radius: 4px;">${s.name}</span>`).join(' ') || ''}
          </span>
        </td>
      </tr>
      ` : ''}
    `;
    }).join('');

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${title} - ${order.order_id}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; color: #333; }
          .header { display: flex; justify-content: space-between; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #10b981; }
          .logo { font-size: 28px; font-weight: bold; color: #10b981; }
          .logo small { display: block; font-size: 12px; color: #666; font-weight: normal; }
          .company-info { font-size: 11px; color: #666; line-height: 1.5; }
          .title { font-size: 24px; color: #333; margin-bottom: 20px; }
          .invoice-meta { background: #f9f9f9; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
          .invoice-meta table { width: 100%; }
          .invoice-meta td { padding: 4px 10px; font-size: 13px; }
          .invoice-meta .label { color: #666; }
          .invoice-meta .value { font-weight: 500; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px; }
          .info-box { background: #f9f9f9; padding: 15px; border-radius: 8px; }
          .info-box h4 { margin: 0 0 10px 0; color: #333; font-size: 14px; text-transform: uppercase; }
          .info-box p { margin: 5px 0; color: #666; font-size: 14px; }
          table.items { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          table.items th { background: #10b981; color: white; padding: 12px; text-align: left; }
          .totals { width: 300px; margin-left: auto; }
          .totals tr td { padding: 8px; border-bottom: 1px solid #eee; }
          .totals tr.total { font-weight: bold; font-size: 16px; }
          .totals tr.total td { border-top: 2px solid #10b981; padding-top: 12px; }
          .promo-row { color: #10b981; }
          .legal-footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e5e5; font-size: 10px; color: #666; line-height: 1.6; }
          .legal-footer strong { color: #333; }
          .preparation-note { background: #fffbeb; border: 1px solid #fbbf24; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="logo">
              FGS
              <small>${legalInfo.tradeName}</small>
            </div>
            <div class="company-info" style="margin-top: 10px;">
              ${legalInfo.address}<br>
              Tél: ${legalInfo.phone} | ${legalInfo.email}
            </div>
          </div>
          <div style="text-align: right;">
            <div style="font-size: 20px; font-weight: bold; color: #333;">${title}</div>
            <div style="color: #666; font-size: 14px; margin-top: 5px;">
              N° ${order.order_id}<br>
              ${new Date(order.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
            </div>
          </div>
        </div>

        ${isInvoice ? `
        <div class="invoice-meta">
          <table>
            <tr>
              <td class="label">SIRET:</td>
              <td class="value">${legalInfo.siret}</td>
              <td class="label">N° TVA:</td>
              <td class="value">${legalInfo.tvaNumber}</td>
            </tr>
            <tr>
              <td class="label">RCS:</td>
              <td class="value">${legalInfo.rcs}</td>
              <td class="label">Code APE:</td>
              <td class="value">${legalInfo.apeCode}</td>
            </tr>
          </table>
        </div>
        ` : `
        <div class="preparation-note">
          <strong>⚠️ Bon de préparation</strong> - Cochez chaque article une fois préparé
        </div>
        `}

        <div class="info-grid">
          <div class="info-box">
            <h4>Client</h4>
            <p><strong>${order.shipping_address?.first_name || ''} ${order.shipping_address?.last_name || ''}</strong></p>
            <p>${order.customer_email || order.email || ''}</p>
            <p>${order.shipping_address?.phone || ''}</p>
          </div>
          <div class="info-box">
            <h4>Adresse de livraison</h4>
            <p>${order.shipping_address?.address_line1 || ''}</p>
            ${order.shipping_address?.address_line2 ? `<p>${order.shipping_address.address_line2}</p>` : ''}
            <p>${order.shipping_address?.postal_code || ''} ${order.shipping_address?.city || ''}</p>
          </div>
        </div>

        <table class="items">
          <thead>
            <tr>
              <th>Désignation</th>
              <th style="text-align: center; width: 80px;">Qté</th>
              ${isInvoice ? `
                <th style="text-align: right; width: 100px;">P.U. HT</th>
                <th style="text-align: right; width: 100px;">Total HT</th>
              ` : `
                <th style="text-align: center; width: 80px;">Préparé</th>
              `}
            </tr>
          </thead>
          <tbody>
            ${itemsHtml}
          </tbody>
        </table>

        ${isInvoice ? `
        <table class="totals">
          <tr>
            <td>Sous-total HT</td>
            <td style="text-align: right;">${subtotalHT.toFixed(2)} €</td>
          </tr>
          ${promoCode ? `
          <tr class="promo-row">
            <td>Remise (${promoCode})</td>
            <td style="text-align: right;">-${promoDiscount.toFixed(2)} €</td>
          </tr>
          ` : ''}
          ${shippingCostTTC > 0 ? `
          <tr>
            <td>Frais de livraison HT</td>
            <td style="text-align: right;">${shippingHT.toFixed(2)} €</td>
          </tr>
          ` : ''}
          <tr>
            <td>TVA (${tvaRate}%)</td>
            <td style="text-align: right;">${totalTVA.toFixed(2)} €</td>
          </tr>
          ${shippingCostTTC === 0 ? `
          <tr>
            <td>Livraison</td>
            <td style="text-align: right; color: #10b981;">GRATUIT</td>
          </tr>
          ` : ''}
          <tr class="total">
            <td>Total TTC</td>
            <td style="text-align: right; color: #10b981;">${(order.total_amount || totalTTC).toFixed(2)} €</td>
          </tr>
        </table>
        ` : ''}

        ${order.order_note ? `
          <div class="info-box" style="margin-top: 20px;">
            <h4>Note client</h4>
            <p>${order.order_note}</p>
          </div>
        ` : ''}

        <div class="legal-footer">
          <strong>${legalInfo.companyName}</strong> - ${legalInfo.legalForm}<br>
          SIRET: ${legalInfo.siret} | RCS: ${legalInfo.rcs} | TVA: ${legalInfo.tvaNumber}<br>
          ${legalInfo.address} | Tél: ${legalInfo.phone}<br><br>
          <strong>Conditions de paiement:</strong> Paiement comptant à la commande.<br>
          <strong>Garantie:</strong> 4 ans sur tous les produits Ajax Systems conformément à la garantie constructeur.<br>
          En cas de litige, le tribunal compétent sera celui de Montpellier.
        </div>
      </body>
      </html>
    `;
  };

  const getColorLabel = (color) => {
    const colors = {
      'black': 'Noir',
      'white': 'Blanc',
      'ivory': 'Ivoire',
      'grey': 'Gris',
      'gray': 'Gris'
    };
    return colors[color?.toLowerCase()] || color || 'Non spécifié';
  };

  const statuses = [
    { value: '', label: 'Tous les statuts' },
    { value: 'pending', label: 'En attente' },
    { value: 'confirmed', label: 'Confirmée' },
    { value: 'shipped', label: 'Expédiée' },
    { value: 'delivered', label: 'Livrée' },
    { value: 'cancelled', label: 'Annulée' },
  ];

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      confirmed: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      shipped: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      delivered: 'bg-green-500/20 text-green-400 border-green-500/30',
      cancelled: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    const labels = {
      pending: 'En attente',
      confirmed: 'Confirmée',
      shipped: 'Expédiée',
      delivered: 'Livrée',
      cancelled: 'Annulée'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-bold text-white">Commandes</h1>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 bg-fgs-surface border border-fgs-border rounded-lg text-white"
        >
          {statuses.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Orders List */}
        <div className="lg:col-span-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-fgs-primary" />
            </div>
          ) : orders.length === 0 ? (
            <div className="bg-fgs-surface border border-fgs-border rounded-xl p-12 text-center">
              <Package className="w-16 h-16 text-fgs-text-muted mx-auto mb-4" />
              <p className="text-fgs-text-muted">Aucune commande</p>
            </div>
          ) : (
            <div className="space-y-3">
              {orders.map(order => (
                <div
                  key={order.order_id}
                  className={`bg-fgs-surface border rounded-xl p-4 transition-colors ${
                    selectedOrder?.order_id === order.order_id 
                      ? 'border-fgs-primary' 
                      : 'border-fgs-border hover:border-fgs-primary/50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span 
                      className="font-mono text-sm text-white cursor-pointer hover:text-fgs-primary"
                      onClick={() => setSelectedOrder(order)}
                    >
                      {order.order_id}
                    </span>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(order.status)}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirm(order.order_id);
                        }}
                        className="p-1.5 hover:bg-red-500/20 rounded text-fgs-text-muted hover:text-red-400 transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div 
                    className="flex items-center justify-between text-sm cursor-pointer"
                    onClick={() => setSelectedOrder(order)}
                  >
                    <span className="text-fgs-text-muted">
                      {new Date(order.created_at).toLocaleDateString('fr-FR', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                    <span className="text-fgs-primary font-mono font-bold">
                      {order.total_amount?.toFixed(2)} €
                    </span>
                  </div>
                  {order.customer_email && (
                    <p className="text-xs text-fgs-text-muted mt-2">{order.customer_email}</p>
                  )}
                  
                  {/* Delete confirmation */}
                  {deleteConfirm === order.order_id && (
                    <div className="mt-3 pt-3 border-t border-fgs-border">
                      <div className="flex items-center gap-2 text-yellow-400 text-sm mb-2">
                        <AlertTriangle className="w-4 h-4" />
                        <span>Supprimer cette commande ?</span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(order.order_id)}
                          className="bg-red-500 hover:bg-red-600"
                        >
                          Supprimer
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setDeleteConfirm(null)}
                          className="border-fgs-border text-fgs-text-muted"
                        >
                          Annuler
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Order Details */}
        <div className="lg:col-span-1">
          {selectedOrder ? (
            <div className="bg-fgs-surface border border-fgs-border rounded-xl p-6 sticky top-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg font-bold text-white">Détails</h3>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handlePrint}
                    className="border-fgs-border text-fgs-text-muted hover:text-white h-8 px-2"
                    title="Imprimer facture"
                  >
                    <Printer className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handlePreparationSheet}
                    className="border-fgs-border text-fgs-text-muted hover:text-white h-8 px-2"
                    title="Bon de préparation"
                  >
                    <FileText className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-fgs-text-muted text-xs">ID Commande</Label>
                  <p className="text-white font-mono text-sm">{selectedOrder.order_id}</p>
                </div>

                <div>
                  <Label className="text-fgs-text-muted text-xs">Client</Label>
                  <p className="text-white text-sm">{selectedOrder.customer_email || selectedOrder.email || 'Non renseigné'}</p>
                </div>

                {/* Shipping Address */}
                {selectedOrder.shipping_address && (
                  <div>
                    <Label className="text-fgs-text-muted text-xs">Adresse de livraison</Label>
                    <div className="mt-1 p-2 bg-fgs-bg rounded text-sm">
                      <p className="text-white">
                        {selectedOrder.shipping_address.first_name} {selectedOrder.shipping_address.last_name}
                      </p>
                      <p className="text-fgs-text-muted">{selectedOrder.shipping_address.address_line1}</p>
                      {selectedOrder.shipping_address.address_line2 && (
                        <p className="text-fgs-text-muted">{selectedOrder.shipping_address.address_line2}</p>
                      )}
                      <p className="text-fgs-text-muted">
                        {selectedOrder.shipping_address.postal_code} {selectedOrder.shipping_address.city}
                      </p>
                      {selectedOrder.shipping_address.phone && (
                        <p className="text-fgs-text-muted text-xs mt-1">{selectedOrder.shipping_address.phone}</p>
                      )}
                    </div>
                  </div>
                )}

                <div>
                  <Label className="text-fgs-text-muted text-xs">Date</Label>
                  <p className="text-white text-sm">
                    {new Date(selectedOrder.created_at).toLocaleDateString('fr-FR', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>
                </div>

                <div>
                  <Label className="text-fgs-text-muted text-xs">Statut</Label>
                  <select
                    value={selectedOrder.status || 'pending'}
                    onChange={(e) => updateOrderStatus(selectedOrder.order_id, e.target.value)}
                    disabled={updatingStatus}
                    className="w-full mt-1 px-3 py-2 bg-fgs-bg border border-fgs-border rounded-lg text-white disabled:opacity-50"
                  >
                    {statuses.filter(s => s.value).map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>

                {/* Tracking Number */}
                <div>
                  <Label className="text-fgs-text-muted text-xs">Numéro de suivi</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={trackingNumber}
                      onChange={(e) => setTrackingNumber(e.target.value)}
                      placeholder="Ex: 1Z999AA10123456784"
                      className="bg-fgs-bg border-fgs-border text-white flex-1"
                    />
                    <Button
                      onClick={() => updateTrackingNumber(selectedOrder.order_id)}
                      size="sm"
                      variant="outline"
                      className="border-fgs-primary text-fgs-primary hover:bg-fgs-primary/10"
                    >
                      <Truck className="w-4 h-4" />
                    </Button>
                  </div>
                  {selectedOrder.tracking_number && (
                    <p className="text-xs text-fgs-primary mt-1">
                      Actuel: {selectedOrder.tracking_number}
                    </p>
                  )}
                </div>

                {/* Order Items with Edit Mode */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-fgs-text-muted text-xs">Articles</Label>
                    {!editingItems ? (
                      <button
                        onClick={() => setEditingItems(true)}
                        className="text-xs text-fgs-primary hover:text-fgs-primary-hover flex items-center gap-1"
                      >
                        <Edit3 className="w-3 h-3" />
                        Modifier
                      </button>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          onClick={saveEditedItems}
                          className="text-xs text-green-400 hover:text-green-300 flex items-center gap-1"
                        >
                          <Save className="w-3 h-3" />
                          Sauvegarder
                        </button>
                        <button
                          onClick={() => {
                            setEditingItems(false);
                            setEditedItems(selectedOrder.items || []);
                          }}
                          className="text-xs text-fgs-text-muted hover:text-white"
                        >
                          Annuler
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    {(editingItems ? editedItems : selectedOrder.items)?.map((item, i) => (
                      <div key={i} className="p-2 bg-fgs-bg rounded">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <span className="text-white text-sm block">{item.name || item.product_name}</span>
                            {/* Affichage de la couleur */}
                            {item.color && (
                              <span className="text-fgs-primary text-xs">
                                Couleur: {getColorLabel(item.color)}
                              </span>
                            )}
                            {/* Affichage des services */}
                            {item.services?.length > 0 && (
                              <div className="mt-1">
                                {item.services.map((service, si) => (
                                  <span key={si} className="text-xs text-fgs-text-muted block">
                                    + {service.name} ({service.price?.toFixed(2)} €)
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {editingItems ? (
                              <>
                                <button
                                  onClick={() => updateItemQuantity(i, -1)}
                                  className="p-1 hover:bg-fgs-surface rounded text-fgs-text-muted hover:text-white"
                                >
                                  <Minus className="w-3 h-3" />
                                </button>
                                <span className="text-white text-sm w-6 text-center">{item.quantity || 1}</span>
                                <button
                                  onClick={() => updateItemQuantity(i, 1)}
                                  className="p-1 hover:bg-fgs-surface rounded text-fgs-text-muted hover:text-white"
                                >
                                  <Plus className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => removeItem(i)}
                                  className="p-1 hover:bg-red-500/20 rounded text-fgs-text-muted hover:text-red-400 ml-1"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </>
                            ) : (
                              <span className="text-fgs-text-muted text-xs">x{item.quantity || 1}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex justify-end mt-1">
                          <span className="text-fgs-primary font-mono text-sm">
                            {((item.price || 0) * (item.quantity || 1)).toFixed(2)} €
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pt-4 border-t border-fgs-border">
                  <div className="flex justify-between">
                    <span className="text-white font-semibold">Total</span>
                    <span className="text-fgs-primary font-mono font-bold text-lg">
                      {editingItems 
                        ? editedItems.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0).toFixed(2)
                        : selectedOrder.total_amount?.toFixed(2)
                      } €
                    </span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="pt-4 border-t border-fgs-border flex gap-2">
                  <Button
                    onClick={handlePrint}
                    variant="outline"
                    className="flex-1 border-fgs-border text-white hover:bg-fgs-surface-highlight"
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    Facture PDF
                  </Button>
                  <Button
                    onClick={handlePreparationSheet}
                    variant="outline"
                    className="flex-1 border-fgs-primary text-fgs-primary hover:bg-fgs-primary/10"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Bon prép.
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-fgs-surface border border-fgs-border rounded-xl p-12 text-center">
              <Eye className="w-12 h-12 text-fgs-text-muted mx-auto mb-4" />
              <p className="text-fgs-text-muted">Sélectionnez une commande</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminOrders;
